// The o¿o mark sting is a fixed 4800 ms animation in four beats. Both the
// WebGL2 shader path and the plain-drawing fallback read their beat from here,
// so the two look alike in time even though they draw differently.
//
//   0 ------ 1000 ---- 1400 ------------ 2700 ---------------- 4800 ms
//   |  tear   |  hold   |      ring      |        fade         |
//                       ^
//                       the hold gate: nothing past here runs until released
//
// The ring and fade wait on the hold gate, which the page releases when the
// session reaches connected or stopped. While the gate is closed the animation
// parks at the end of the hold and stops asking for frames.

export const STING_TEAR_END_MS = 1000;
export const STING_HOLD_END_MS = 1400;
export const STING_RING_END_MS = 2700;
export const STING_END_MS = 4800;

/**
 * The same four beat lengths as the marks above, expressed as durations
 * instead of absolute marks. The Phaser scene schedules its tweens off these
 * (tear is split into a stagger and a strip length that sum to the tear
 * beat), while the React canvas paths read the absolute marks above.
 */
export const STING_BEATS = {
  tearStagger: 400,
  tearStrip: STING_TEAR_END_MS - 400,
  hold: STING_HOLD_END_MS - STING_TEAR_END_MS,
  ring: STING_RING_END_MS - STING_HOLD_END_MS,
  fade: STING_END_MS - STING_RING_END_MS,
} as const;

/** Outer radius and inner-ring proportions the ring pulse draws with. */
export const STING_RING = {
  outerRadius: 270,
  innerScale: 0.62,
  innerAlpha: 0.45,
} as const;

export type StingBeat = 'tear' | 'hold' | 'ring' | 'fade' | 'done';

export type StingFrame = {
  beat: StingBeat;
  /** How far through this beat, 0 at its first instant and approaching 1 at its last. */
  progress: number;
};

/**
 * Map a time in milliseconds since the sting started onto its beat.
 *
 * `released` is the state of the hold gate. When it is false, any time at or
 * past the end of the hold reports a finished hold, so the caller parks there.
 */
export function stingBeatAt(elapsedMs: number, released: boolean): StingFrame {
  const t = elapsedMs > 0 ? elapsedMs : 0;

  if (t < STING_TEAR_END_MS) {
    return { beat: 'tear', progress: t / STING_TEAR_END_MS };
  }

  if (t < STING_HOLD_END_MS) {
    return {
      beat: 'hold',
      progress: (t - STING_TEAR_END_MS) / (STING_HOLD_END_MS - STING_TEAR_END_MS),
    };
  }

  if (!released) {
    return { beat: 'hold', progress: 1 };
  }

  if (t < STING_RING_END_MS) {
    return {
      beat: 'ring',
      progress: (t - STING_HOLD_END_MS) / (STING_RING_END_MS - STING_HOLD_END_MS),
    };
  }

  if (t < STING_END_MS) {
    return {
      beat: 'fade',
      progress: (t - STING_RING_END_MS) / (STING_END_MS - STING_RING_END_MS),
    };
  }

  return { beat: 'done', progress: 1 };
}

/**
 * A one-way latch the sting waits on before the ring pulse.
 *
 * `release()` is safe to call more than once and safe to call before anyone is
 * waiting. Waiters registered after release run immediately.
 */
export class HoldGate {
  private open = false;
  private waiting: Array<() => void> = [];

  get released(): boolean {
    return this.open;
  }

  release(): void {
    if (this.open) return;
    this.open = true;
    const waiters = this.waiting;
    this.waiting = [];
    for (const waiter of waiters) waiter();
  }

  whenReleased(callback: () => void): void {
    if (this.open) {
      callback();
      return;
    }
    this.waiting.push(callback);
  }
}
